import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import data from './goldmedalistsinfo.json';
import './dataviz.scss';
import Tooltip from './Tooltip';
import ImageScroll from './ImagesScroll';

const CareerType = () => {
    
    const svgRef = useRef();
    const containerRef = useRef();

    const [tooltipContent, setTooltipContent] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const [hovered, setHovered] = useState(null);

    const isMobile = window.innerWidth <= 768;

    useEffect(() => {
        drawChart();

        const handleResize = debounce(() => {
            drawChart();
        }, 100); // Adjust the debounce time as needed

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []); 
    
    // Debounce function to limit how often drawChart is called
    const debounce = (func, delay) => {
        let timeout;
        return (...args) => {
            if (timeout) clearTimeout(timeout);
            timeout = setTimeout(() => {
                func(...args);
            }, delay);
        };
    };
    

    const sports = Array.from(new Set(data.map(d => d.sport)));

    const careerTypes = ["Sports Admin. Government", "Non-Sports Government", "Higher Education", "Coaching", "Other (Within System)", "Business/Entrepreneurship", "Entertainment", "Other (Outside System)", "Family Life"];

    const careerCountData = sports.map(sport => {
        const sportData = data.filter(d => d.sport === sport);
        return {
          sport,
          "Sports Admin. Government": sportData.filter(d => d.postRetirement === "从政").length,
          "Non-Sports Government": sportData.filter(d => d.postRetirement === "非体育类的从政").length,
          "Higher Education": sportData.filter(d => d.postRetirement === "高校任职").length,
          "Coaching": sportData.filter(d => d.postRetirement === "教练").length,
          "Other (Within System)": sportData.filter(d => d.postRetirement === "体制内其他").length,
          "Business/Entrepreneurship": sportData.filter(d => d.postRetirement === "创业/经商").length,
          "Entertainment": sportData.filter(d => d.postRetirement === "演艺").length,
          "Other (Outside System)": sportData.filter(d => d.postRetirement === "体制外其他").length,
          "Family Life": sportData.filter(d => d.postRetirement === "回归家庭").length,
        //   "不详": sportData.filter(d => d.postRetirement === "不详").length
        };
      });

      const athleteCountperSport = careerCountData.map((d) => {
        return {
          ...d,
          total: Object.values(d).reduce((acc, val) => {
            if (typeof val === "number") return acc + val;
            return acc;
          }, 0),
        };
      });

      athleteCountperSport.sort((a, b) => a.total - b.total);
      
      const color = d3.scaleOrdinal()
        .domain(careerTypes)
        .range([
            "#ED6E79", // 从政
            "#FE89B5", // 非体育类的从政
            "#DA96DF", // 高校任职
            "#F5A26E", // 教练
            "#FFDC8A", // 体制内其他
            "#658DE5", // 创业/经商 (外)
            "#898CFF", // 演艺
            "#59D0E4", // 体制外其他
            "#90D4F7", // 回归家庭
            ]);

    //legend
    const legendTooltip = {
        "Non-Sports Government": "<div class='tooltip__details'>Positions included Deputy Director-General of the China Disabled Persons' Federation and Deputy Secretary of the Party Leadership Group.</div>",
        "Other (Within System)": "<div class='tooltip__details'>Gymnastics champion Li Shanshan joined Bank of China Macau Branch.</div>",
        "Other (Outside System)": "<div class='tooltip__details'>Including social welfare, writer, independent media, and nursing work, etc.</div>",
    };


    const drawChart = () => {
        // const margin = { top: 20, right: 30, bottom: 40, left: 60 };
        // const width = 960 - margin.left - margin.right;
        // const height = 800 - margin.top - margin.bottom;

        // const svg = d3.select(svgRef.current)
        // .attr('width', width + margin.left + margin.right)
        // .attr('height', height + margin.top + margin.bottom)
        // .append('g')
        // .attr('transform', `translate(${margin.left},${margin.top})`);

        const container = d3.select(containerRef.current);
        const svg = d3.select(svgRef.current);
        const containerWidth = container.node().clientWidth;
        const containerHeight = container.node().clientHeight;

        const margin = { top: 40, right: 10, bottom: 40, left: 10 };
        const width = containerWidth - margin.left - margin.right;
        const height = containerHeight - margin.top - margin.bottom;

        svg
        .attr('viewBox', `0 0 ${containerWidth} ${containerHeight}`)
        .attr('preserveAspectRatio', 'xMinYMin meet')
        .attr('width', '100%')
        .attr('height', '100%');


        const svgGroup = svg
        .selectAll('g')
        .data([null])
        .join('g')
        .attr('transform', `translate(${margin.left},${margin.top})`);

        
        const filteredCareerData = athleteCountperSport.filter(d => d.sport !== 'Snowboard' && d.sport !== 'Artistic Gymnastics' && d.sport !== 'Canoe Sprint');

        const y = d3.scaleBand()
        .domain(filteredCareerData.map(d => d.sport))
        .range([height, 0])
        .paddingInner(0.45)
        .paddingOuter(0.1);


        //体制外
        const stackRight = d3.stack()
        .keys(["Business/Entrepreneurship", "Entertainment", "Other (Outside System)", "Family Life"])
        .order(d3.stackOrderNone)
        .offset(d3.stackOffsetNone);

        //体制内
        const stackLeft = d3.stack()
        .keys(["Sports Admin. Government", "Non-Sports Government", "Higher Education", "Coaching", "Other (Within System)"])
        .order(d3.stackOrderNone)
        .offset(d3.stackOffsetNone);

        const seriesRight = stackRight(careerCountData);
        const seriesLeft = stackLeft(careerCountData);

        const seriesCount = (data) => {
            const countLeft = data["Sports Admin. Government"] + data["Non-Sports Government"] + data["Higher Education"] + data["Coaching"] + data["Other (Within System)"];
            const countRight = data["Business/Entrepreneurship"] + data["Entertainment"] + data["Other (Outside System)"] + data["Family Life"];
            return { countLeft, countRight };
            
        };

        const xRight = d3.scaleLinear()
            .domain([0, 20])
            .range([width / 2 + 65, width]);

        const xAxisRight = d3.axisBottom(xRight)
            .ticks(5)
            .tickSize(0);

        const xLeft = d3.scaleLinear()
            .domain([0, 20])
            .range([width / 2 - 70, 0]);

        const xAxisLeft = d3.axisBottom(xLeft)
            .ticks(5)
            .tickSize(0);




//row highlight
        svgGroup.selectAll(".row-highlight")
            .data(filteredCareerData)
            .enter()
            .append("rect")
            .attr("class", "row-highlight")
            .attr("x", 0)
            .attr("y", (d) => y(d.sport) - 5)
            .attr("width", width)
            .attr("height", y.bandwidth() + 10)
            .attr("fill", "white")
            .attr("opacity", 0)
            // .lower()
            ;
            

        


//體制外
        svgGroup.selectAll(".serie-right")
        .data(seriesRight)
        .enter().append("g")
        .attr("fill", d => color(d.key))
        .selectAll("rect")
        // .data(d => d)
        .data(d => {
            return d.map(v => ({ ...v, key: d.key }));
        })
        .enter().append("rect")
        .attr("y", d => y(d.data.sport))
        .attr("x", d => xRight(d[0]))
        .attr("height", y.bandwidth())
        .attr("width", d => xRight(d[1]) - xRight(d[0]))
        .on("mouseover", (event, d) => {
                const [x, y] = d3.pointer(event, containerRef.current);
            
                const sport = d.data.sport;
                const careerType = d.key;
                const count = d.data[careerType];
                // setTooltipContent(`<div class="tooltip__content">
                // <span class="tooltip__details"><b>${count}</b> ${count > 1 ? `athletes` : `athlete`} in ${count > 1? `` : `a`} <b>${careerType}</b> ${count > 1 ? `careers` : `career`} after retirement.</span> </div>`);
                setTooltipContent( `<div class="tooltip__content"><span class="tooltip__details">
                    <b>${count}</b> ${count > 1 ? `athletes` : `athlete`} in ${ careerType == 'Family Life' ? `<b>` + careerType + `</b>` + `` : count > 1 ? `<b>` + careerType + `</b>` + ` careers` : `<b>` + careerType + `</b> career` } after retirement.

                    </span></div>`)
                // setTooltipPosition({ x: event.pageX, y: event.pageY })
                setTooltipPosition({ x: event.clientX + window.scrollX, 
                    y: event.clientY + window.scrollY  })
                setHovered(event.target);

                svg.selectAll(".row-highlight")
                .filter((row) => row.sport === sport)
                .attr('opacity', 0.8);
            })
        .on("mouseout", () => {
            setTooltipContent(null);
            setHovered(null);
            svg.selectAll(".row-highlight")
            .attr("opacity", 0);
        });

//體制內
        svgGroup.selectAll(".serie-left")
        .data(seriesLeft)
        .enter().append("g")
        .attr("class", "serie-left")
        .attr("fill", d => color(d.key))
        .selectAll("rect")
        .data(d => {
            return d.map(v => ({ ...v, key: d.key }));
        })
        .enter().append("rect")
        .attr("y", d => y(d.data.sport))
        .attr("x", d => xLeft(d[1])) // Start from the left
        .attr("height", y.bandwidth())
        .attr("width", d => xLeft(d[0]) - xLeft(d[1])) // Width is the difference between start and end
        .on("mouseover", (event, d) => {
            const sport = d.data.sport;
            const careerType = d.key;
            const count = d.data[careerType];
            setTooltipContent(`
                <div class="tooltip__content">
                <span class="tooltip__details"><b>${count}</b> ${count > 1 ? `athletes` : `athlete`} in ${count > 1? `` : `a`} <b>${careerType}</b> ${count > 1 ? `careers` : `career`} after retirement.</span> 
                </div>
                `);
            setTooltipPosition({ x: event.pageX - 0, y: event.pageY - 0 });
            setHovered(event.target);
            
            svg.selectAll(".row-highlight")
                .filter((row) => row.sport === sport)
                .attr('opacity', 0.8);
        })
    .on("mouseout", () => {
        setTooltipContent(null);
        setHovered(null);
        svg.selectAll(".row-highlight")
            .attr("opacity", 0);
    });

        
        // Add y-axis for sports
        svgGroup.append("g")
            .attr("class", "y-axis")
            .attr("transform", `translate(${width / 2}, 0)`) // Move to the middle
            .call(d3.axisLeft(y).tickSize(0))
            .selectAll("text")
            .attr("text-anchor", "middle")
            .on("mouseover", function (event, d) {
                const counts = careerCountData.find(data => data.sport === d);
                const { countLeft, countRight } = seriesCount(counts);

                svg.selectAll(".row-highlight")
                .filter((row) => row.sport === d)
                .attr('opacity', 0.8);

                setTooltipContent (`
                    <div class="tooltip__content">
                    <span class="tooltip__title"><b>${d}</b></span>
                    <span class="tooltip__details">
                    Total Within to Total Outside</br>
                    <b><span style="color:#ED6E79">${countLeft}</span> : <span style="color:#658DE5">${countRight}</span></b>
                    </span>
                    </div>
                    `);
                setTooltipPosition({ x: event.pageX + 10, y: event.pageY + 10 });
            })
            .on("mouseout", () => {
                svg.selectAll(".row-highlight")
                .attr("opacity", 0);
                setTooltipContent(null);
                setHovered(null);
            })
            ;

        // Hide y line
        svgGroup.selectAll("path, line")
            .attr("display", "none");

        svgGroup.append("g")
            .attr("class", "grid-lines")
            .selectAll("line")
            .data(xRight.ticks(5))
            .enter().append("line")
            .attr("x1", d => xRight(d))
            .attr("x2", d => xRight(d))
            .attr("y1", 0)
            .attr("y2", height)
            .lower();

        svgGroup.append("g")
            .attr("class", "grid-lines")
            .selectAll("line")
            .data(xLeft.ticks(5))
            .enter().append("line")
            .attr("x1", d => xLeft(d))
            .attr("x2", d => xLeft(d))
            .attr("y1", 0)
            .attr("y2", height);

        // x-axis careers right
        svgGroup.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0,${height})`)
            .call(xAxisRight)
            .call(g => g.select(".domain").remove());

        // x-axis careers left
        svgGroup.append("g")
            .attr("class", "x-axis")
            .attr("transform", `translate(0, ${height})`)
            .call(xAxisLeft)
            .call(g => g.select(".domain").remove());

        //chart-titles
        svgGroup.selectAll('.chart-title').remove(); 

        svgGroup.append('text')
            .attr('class', 'chart-title')
            .attr('x', isMobile ? xLeft(3) : xLeft(0) - 70)
            .attr('y', -25)
            .attr('text-anchor', 'middle')
            .style('font-size', isMobile ? '12px' : '17px')
            .text('Within State System');

        svgGroup.append('text')
            .attr('class', 'chart-title')
            .attr('x', isMobile ? xRight(3) : xRight(0) + 70)
            .attr('y', -25)
            .attr('text-anchor', 'middle')
            .style('font-size', isMobile ? '12px' : '17px')
            .text('Outside State System');
      

        return () => {
            svgGroup.selectAll('*').remove();
        }
    }

    const handleLegendMouseOver = (e, type) => {
        const normalizedType = type.replace(/\s+/g, '_').replace(/[^\w\s]/g, ''); 
        const tooltipContent = legendTooltip[type];

        if (tooltipContent) {
            setTooltipContent(tooltipContent);
            // setTooltipPosition({ x: e.pageX, y: e.pageY });
            // setTooltipPosition({ x: e.clientX + window.scrollX, 
            //     y: e.clientY + window.scrollY })
            setTooltipPosition({ x: e.clientX + window.scrollX, y: e.clientY + window.scrollY })
            setHovered(normalizedType);
            // console.log(`tooltipPosition:` );
        }
    };

    const handleLegendMouseOut = () => {
        setTooltipContent(null);
        setHovered(null);
    };

    return (
        <React.Fragment>
            <div className='content content__heading'>
                <h2>I. Most Olympic champions remain within the state-sponsored sport system after retirement</h2>
                </div>
            <div ref={containerRef} className='chart-container' >
                
                <svg ref={svgRef} id='career-type' />
                <Tooltip content={tooltipContent} position={tooltipPosition} hovered={hovered} />
            </div>
            
            <div className='content'>
            <div className='legend' style={{ justifyContent: 'center', paddingBottom:'20px' }}>
                {careerTypes.map((type, i) => (
                    <div key={type} className='legend__item'
                    onMouseOver={(e) => handleLegendMouseOver(e, type)}
                    onMouseOut={handleLegendMouseOut}
                    >
                        <div className="legend__color" style={{ backgroundColor: color(type) }}></div>
                        <div className="legend__label">
                            {type}
                            {legendTooltip[type] && (
                            <span className="legend__asterisk">*</span>
                        )}
                            </div>
                    </div>
                ))}
                </div>

                <div className='scroll__caption chart-caption'>Note: Hover over the chart to view specific employment information of retired champions in different events.</div>

                {/* <div className='content'> */}
                    <div className='content__text'>
                        <p>Unlike the Western model of <a href='https://www.sport.gov.cn/n322/n3407/n3411/c564455/content.html' target='_blank'>community-based management</a>, China follows the Soviet-style "state-sponsored sports system," where athletes are directly selected and trained by national sports agencies. Since the era of a planned economy, sports have been separated from general education in China, and professional sports have further branched off from general sports, becoming a specialized "occupation." Retired athletes traditionally benefited from a state-assigned job placement policy. After retirement, in addition to becoming coaches, most Olympic champions "iron rice bowl" government positions within the sports system, often taking roles such as deputy directors of sports bureaus.</p>
                        <p>By the <a href='https://francis-press.com/uploads/papers/HWfKyt4dWLu7i14FFHcRMKT0YkAFgPwAKVscAQMo.pdf' target='_blank'>1980s</a>, as positions within the sports administration system became saturated, retired athletes started transitioning into non-sports-related government sectors. However, data shows that only a small fraction of Olympic champions have pursued political careers outside the sports system. Those who did typically began their political journey within sports institutions and gradually climbed the ranks, assuming roles such as vice chairperson of the Women's Federation or secretary of the Communist Youth League at the provincial level.</p>
                        <p>Around 2000, as China expanded university admissions, a series of national and local policies allowed retired athletes to enter higher education institutions without taking <a href='http://xb.hepec.edu.cn/ch/reader/create_pdf.aspx?file_no=20220406&flag=1&year_id=2022&quarter_id=4' target='_blank'>entrance exams</a>. This policy broadened the possibilities for Olympic champions' "second life." An increasing number of them pursued academic positions in sports universities or general institutions, often obtaining titles such as associate professor or even dean.</p>

                    </div>
                {/* </div> */}
                {/* <Image src='/images/AP920803044.jpg' caption='1992年8月3日，中国乒乓球队员邓亚萍（右）和队友乔红在巴塞罗那夺得金牌后握手。（AP / Craig Fujii）' /> */}

                <ImageScroll
                    src1='/images/AP920803044.jpg'
                    caption1='August 3, 1992, Chinese table tennis players Deng Yaping (right) and her teammate Qiao Hong shake hands after winning the gold medal in Barcelona.（AP / Craig Fujii）'
                    src2='/images/000_Hkg1106910.jpg'
                    caption2={"After retiring, Deng Yaping went abroad for further studies, and served as the Deputy Secretary of the Beijing Municipal Committee of the Communist Youth League and the Deputy Secretary-General of the People's Daily. Later, she established the \"Deng Yaping Sports Industry Investment Fund,\" among other ventures. In 2008, Deng Yaping and others held a press conference during the \"Two Sessions.\" (AFP / Frederic J. Brown)"}
                    />

                {/* <Image src='/images/000_Hkg1106910.jpg' caption='2008年3月12日，邓亚萍（左）与北京市副市长刘敬民（左二）、和中国体育部副部长崔大林（右）以及中电影导演张艺谋（右二）在北京人民大会堂“两会”间隙举行新闻发布会。(AFP / Frederic J. BROWN)' /> */}
            </div>

        </React.Fragment>
    );

};

export default CareerType;
